import pdfMake from 'pdfmake/build/pdfmake';
import vfsFontsRaw from 'pdfmake/build/vfs_fonts?raw';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';

// pdfmake 0.3.x font virtual file system ---
// `pdfmake/build/vfs_fonts.js` is a plain script that declares `var vfs = {...}`
// (the Roboto *.ttf base64 blobs). It has NO ESM/CJS exports, so importing it as
// a module yields `undefined`/empty object and the font dictionary never reaches
// pdfmake. Every PDF then fails with "File 'Roboto-Regular.ttf' not found in
// virtual file system". Reading it as raw text and evaluating it in a function
// scope recovers the `vfs` object reliably across bundlers.
function loadVfs(): Record<string, string> {
  try {
    // eslint-disable-next-line no-new-func
    return new Function(
      `${vfsFontsRaw};\nreturn (typeof vfs !== 'undefined') ? vfs : null;`,
    )() as Record<string, string>;
  } catch {
    return {};
  }
}

const vfs = loadVfs();
if (vfs && Object.keys(vfs).length > 0) {
  if (typeof (pdfMake as any).addVirtualFileSystem === 'function') {
    (pdfMake as any).addVirtualFileSystem(vfs);
  } else {
    (pdfMake as any).vfs = vfs;
  }
}

export function createPdf(doc: TDocumentDefinitions) {
  return pdfMake.createPdf(doc);
}

/**
 * Render a pdfmake document definition on the server and return a PDF Blob URL.
 * This bypasses the client-side pdfmake Web Worker (which can hang in some
 * browsers), producing a reliable preview/download. The server renders PNG/JPEG
 * data: URLs but NOT raw SVG — rasterize any vector graphics on the client first.
 */
export async function generatePdfBlobViaServer(
  docDef: TDocumentDefinitions,
  timeoutMs = 15000,
): Promise<string> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch('/api/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ docDef }),
      signal: controller.signal,
    });
    if (!res.ok) {
      let detail = '';
      try {
        const j = await res.json();
        detail = j.error || j.detail || '';
      } catch {
        /* ignore */
      }
      throw new Error(`PDF generation failed (${res.status})${detail ? ': ' + detail : ''}`);
    }
    const blob = await res.blob();
    if (!blob || blob.size === 0) throw new Error('PDF generation returned an empty document');
    return URL.createObjectURL(blob);
  } finally {
    clearTimeout(timer);
  }
}

export default pdfMake;
